import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { Logger } from '@app/services/logger/logger.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { TenantValidator } from '@app/utils/validators/tenant-validator';
import { TranslatePipe } from '@ngx-translate/core';
import { EnterpriseTenant, ProvisioningTypes } from '@skyfit/core-data-library';

import { ButtonComponent, NativeButtonType } from '@app/components/button';
import { CardComponent } from '@app/components/card';
import { FormActionButtonComponent } from '@app/components/form-action-button';
import { SpinnerComponent } from '@app/components/spinner';
import { ToastService, ToastType } from '@app/components/toast';
import { SaveChangesService } from '@app/services/save-changes/save-changes.service';
import { Spacing } from '@app/shared/types';
import { TenantConfigurationService } from '../tenant-configuration.service';
import { TenantDialogComponent } from '../tenant-dialog/tenant-dialog.component';
import { GeneralFormComponent } from './general-form/general-form.component';

@Component({
  selector: 'app-general',
  standalone: true,
  imports: [
    CardComponent,
    FormActionButtonComponent,
    CommonModule,
    SpinnerComponent,
    GeneralFormComponent,
    TenantDialogComponent,
    ButtonComponent,
    TranslatePipe,
  ],
  templateUrl: './general.component.html',
  styleUrl: './general.component.css',
})
export class GeneralComponent implements OnInit {
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly toastService = inject(ToastService);
  private readonly translatorService = inject(TranslatorService);
  private readonly tenantValidator = new TenantValidator();
  private readonly saveChangesService = inject(SaveChangesService);
  private readonly logger = inject(Logger);
  tenantConfigurationService = inject(TenantConfigurationService);
  readonly destroyRef = inject(DestroyRef);

  tenant = this.adminTenantService.getTenant();
  dialogOpen = this.tenantConfigurationService.isAddNewTenantDialogOpen;
  isConfigurationsValid = computed(
    () =>
      !this.nameError() &&
      !this.descriptionError() &&
      !this.slugsError() &&
      !this.emailsError()
  );
  name = signal('');
  description = signal('');
  slugs = signal('');
  emails = signal('');
  provisioningType = signal<ProvisioningTypes>(ProvisioningTypes.emailRegex);

  nameError = computed(
    () =>
      this.tenantValidator.validateName(this.name()).validationObj.error.name ??
      ''
  );
  descriptionError = computed(
    () =>
      this.tenantValidator.validateDescription(this.description()).validationObj
        .error.description ?? ''
  );
  slugsError = computed(
    () =>
      this.tenantValidator.validateSlugs(this.slugs()).validationObj.error
        .slugs ?? ''
  );
  emailsError = computed(() => {
    if (this.provisioningType() === ProvisioningTypes.emailRegex) {
      return (
        this.tenantValidator.validateEmails(this.emails(), true).validationObj
          .error.emails ?? ''
      );
    }
    return '';
  });

  private initialName = signal<string | undefined>(undefined);
  private initialDescription = signal<string | undefined>(undefined);
  private initialSlugs = signal<string | undefined>(undefined);
  private initialEmails = signal<string | undefined>(undefined);
  private initialProvisioningType = signal<ProvisioningTypes | undefined>(
    undefined
  );

  hasChanges = computed(() => {
    return (
      this.initialName() !== undefined &&
      (this.name() !== this.initialName() ||
        this.description() !== this.initialDescription() ||
        this.slugs() !== this.initialSlugs() ||
        this.emails() !== this.initialEmails() ||
        this.provisioningType() !== this.initialProvisioningType())
    );
  });

  Spacing = Spacing;
  NativeButtonType = NativeButtonType;

  ngOnInit(): void {
    this.name.set(this.tenant.name ?? '');
    const rawDescription =
      this.tenant.description || `${this.tenant.name ?? ''} description`;
    this.description.set(rawDescription);
    const defaultSlug =
      this.tenant.name
        ?.toLowerCase()
        .replace(/[^a-z0-9 ]/g, '')
        .replace(/\s+/g, '-') ?? '';
    this.slugs.set(this.tenant.config?.slugs?.value?.join() || defaultSlug);
    this.emails.set(this.tenant.config?.emailDomains?.value?.join() || '.*');
    this.provisioningType.set(
      this.parseProvisioningType(this.tenant.config?.provisioningType?.value)
    );

    this.updateInitialValues();
  }

  constructor() {
    effect(() => {
      this.saveChangesService.setHasChanges(this.hasChanges());
    });
  }

  private updateInitialValues(): void {
    this.initialName.set(this.name());
    this.initialDescription.set(this.description());
    this.initialSlugs.set(this.slugs());
    this.initialEmails.set(this.emails());
    this.initialProvisioningType.set(this.provisioningType());
  }

  private parseProvisioningType(value?: string): ProvisioningTypes {
    const map: Record<string, ProvisioningTypes> = {
      'SSO/OAuth': ProvisioningTypes.SSO_OAuth,
      emailRegex: ProvisioningTypes.emailRegex,
      manual: ProvisioningTypes.manual,
      eligibilityFile: ProvisioningTypes.eligibilityFile,
    };

    return value && map[value] ? map[value] : ProvisioningTypes.emailRegex;
  }

  closeDialog() {
    this.tenantConfigurationService.toggleAddNewTenantDialog();
  }

  saveTenant(tenant: EnterpriseTenant) {
    if (!this.isConfigurationsValid()) {
      const message = this.translatorService.translate(
        'tenantConfiguration.general.updateError'
      );
      this.toastService.add(message, ToastType.ERROR);
      return;
    }
    this.adminTenantService.createTenant(tenant).subscribe({
      next: () => {
        const message = this.translatorService.translate(
          'tenantConfiguration.general.saveSuccess'
        );
        this.toastService.add(message, ToastType.INFO);
        this.closeDialog();
      },
      error: (error) => {
        this.logger.error(
          `[GeneralComponent] Error creating tenant for tenant '${this.name()}': ${
            error.message
          }`
        );
        const message = this.translatorService.translate(
          'tenantConfiguration.general.saveError'
        );
        this.toastService.add(message, ToastType.ERROR);
      },
    });
  }

  handleEdit() {
    if (this.tenantConfigurationService.isReadMode()) {
      this.tenantConfigurationService.isReadMode.set(false);
    } else {
      this.onSubmit();
    }
  }

  onSubmit() {
    if (!this.isConfigurationsValid()) {
      const message = this.translatorService.translate(
        'tenantConfiguration.general.updateError'
      );
      this.toastService.add(message, ToastType.ERROR);
      return;
    }

    this.adminTenantService
      .updateTenantGeneralInfo({
        name: this.name(),
        description: this.description().trim(),
        slugs: this.slugs(),
        emails: this.emails(),
        provisioningType: this.provisioningType(),
      })
      .subscribe({
        next: () => {
          const message = this.translatorService.translate(
            'tenantConfiguration.general.updateSuccess'
          );
          this.toastService.add(message, ToastType.INFO);
          this.updateInitialValues();
          this.tenantConfigurationService.isReadMode.set(true);
        },
        error: (error) => {
          this.logger.error(`Error Updating tenant ${error.message}`);

          if (error.message) {
            this.toastService.add(error.message, ToastType.ERROR);
          } else {
            const message = this.translatorService.translate(
              'tenantConfiguration.general.updateError'
            );
            this.toastService.add(message, ToastType.ERROR);
          }
        },
      });
  }
}
